import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useInView } from 'react-intersection-observer';
import { useDebounce } from 'usehooks-ts';
import {
  getGetAllUsersQueryKey,
  useActivateUsers,
  useCreateUser,
  useDeactivateUsers,
  useGetAllUsersInfinite,
  useGetDeactivatedUsers,
  useGetUsersCount,
  useUpdateUser,
} from '@juno/client-api';
import {
  JunoUser,
  MentorshipStatusEnum,
  RoleEnum,
  Site,
  UserDeactivation,
} from '@juno/client-api/model';
import { FeatureConfig } from '@juno/client-api/utils';
import { FEATURE_CONFIG_KEYS_ENUM } from '@juno/constants';
import {
  ConfirmDialog,
  DeactivateUserDialog,
  DefaultSearchSortFilter,
  DrawerHeader,
  EditUserForm,
  RightExpandableDrawer,
  ScrollableChips,
} from '@juno/ui';
import { Container } from '@juno/ui';
import { MutationAction, calculateUserSearchFilters, onMutation, useSettings } from '@juno/utils';
import { AccessibleCheckbox, UserListItem, UserListSkeleton, searchStyles } from './helpers';

const PAGE_SIZE = 15;

interface UserPanelProps {
  backupPlatformId?: string;
  isSuperAdmin?: boolean;
}

const UsersPanel: React.FC<UserPanelProps> = ({ backupPlatformId = '', isSuperAdmin = false }) => {
  const { configs, site, isStaff } = useSettings();
  const isViewerStaff = isStaff || isSuperAdmin;
  const platformId = site?.platform_id || backupPlatformId;
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchValue = useDebounce(searchValue, 200);
  const [currentUser, setCurrentUser] = useState<JunoUser | null>(null);
  const [sort, setSort] = useState('last_name');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [viewEditOpen, setViewEditOpen] = useState(false);
  const [createNewOpen, setCreateNewOpen] = useState(false);
  const [deactivateUserOpen, setDeactivateUserOpen] = useState(false);
  const [successfullySubmitted, setSuccessfullySubmitted] = useState(false);
  const [isBulkSelecting, setIsBulkSelecting] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Feature flag for mentorship management
  const mentorshipEnabled = FeatureConfig.getFeatureFlag(
    configs,
    FEATURE_CONFIG_KEYS_ENUM.Mentorship,
  );

  const { data: userDeactivations, refetch: refetchDeactivations } = useGetDeactivatedUsers(
    platformId || backupPlatformId,
    {
      order: '-date_created',
    },
  );

  // search query
  const searchFilter = useMemo(() => {
    if (!debouncedSearchValue) return {};
    return calculateUserSearchFilters(debouncedSearchValue);
  }, [debouncedSearchValue]);

  let filter_and = {
    ...(selectedFilter === 'admins' && { role: RoleEnum.NUMBER_1 }),
    ...(selectedFilter === 'deactivated' && { active: false }),
    ...(selectedFilter === 'members' && { role: RoleEnum.NUMBER_0 }),
    ...(selectedFilter === 'staff' && { role: RoleEnum.NUMBER_2 }),
  };

  // When the mentorship feature flag is enabled, the following filters will be shown
  if (mentorshipEnabled || isSuperAdmin) {
    filter_and = {
      ...filter_and,
      ...(selectedFilter === 'mentors' && { mentorship_status: MentorshipStatusEnum.NUMBER_0 }),
      ...(selectedFilter === 'mentees' && { mentorship_status: MentorshipStatusEnum.NUMBER_1 }),
    };
  }

  const {
    data: allUsers,
    isLoading: isLoadingUsers,
    isFetching: isFetchingUsers,
    refetch: refetchUsers,
    fetchNextPage,
  } = useGetAllUsersInfinite(
    platformId,
    {
      ...searchFilter,
      filter_and,
      order: sort,
      limit: PAGE_SIZE,
      offset: 0,
    },
    {
      query: {
        queryKey: [
          getGetAllUsersQueryKey(platformId, {
            ...searchFilter,
            filter_and,
            limit: PAGE_SIZE,
            order: sort,
            offset: 0,
          }),
        ],
        getNextPageParam: (lastPage, allPages) => allPages.length || 0,
        initialPageParam: 0,
        enabled: !!platformId,
      },
    },
  );

  const { data: allUsersCount, refetch: refetchAllUsersCount } = useGetUsersCount(
    platformId,
    {
      filter: {
        platform_id: platformId,
      },
    },
    { query: { enabled: !!platformId } },
  );
  const { data: adminUsersCount, refetch: refetchAdminUsersCount } = useGetUsersCount(
    platformId,
    {
      filter: {
        platform_id: platformId,
        role: RoleEnum.NUMBER_1,
      },
    },
    { query: { enabled: !!platformId } },
  );
  const { data: deactivatedUsersCount, refetch: refetchDeactivatedUsersCount } = useGetUsersCount(
    platformId,
    {
      filter: {
        platform_id: platformId,
        active: false,
      },
    },
    { query: { enabled: !!platformId } },
  );
  const { data: memberUsersCount, refetch: refetchMemberUsersCount } = useGetUsersCount(
    platformId,
    {
      filter: {
        platform_id: platformId,
        role: RoleEnum.NUMBER_0,
      },
    },
    { query: { enabled: !!platformId } },
  );
  const { data: staffUsersCount, refetch: refetchStaffUsersCount } = useGetUsersCount(
    platformId,
    {
      filter: {
        platform_id: platformId,
        role: RoleEnum.NUMBER_2,
      },
    },
    { query: { enabled: !!platformId } },
  );
  const { data: mentorUsersCount, refetch: refetchMentorUsersCount } = useGetUsersCount(
    platformId,
    {
      filter: {
        platform_id: platformId,
        mentorship_status: MentorshipStatusEnum.NUMBER_0,
      },
    },
    { query: { enabled: !!platformId } },
  );
  const { data: menteeUsersCount, refetch: refetchMenteeUsersCount } = useGetUsersCount(
    platformId,
    {
      filter: {
        platform_id: platformId,
        mentorship_status: MentorshipStatusEnum.NUMBER_1,
      },
    },
  );

  const refetch = () => {
    refetchUsers();
    refetchDeactivations();
    refetchAllUsersCount();
    refetchAdminUsersCount();
    refetchDeactivatedUsersCount();
    refetchMemberUsersCount();
    refetchStaffUsersCount();
    refetchMentorUsersCount();
    refetchMenteeUsersCount();
    setSuccessfullySubmitted(true);
    setConfirmDialogOpen(false);
  };

  const userCreate = useCreateUser(onMutation(MutationAction.CREATE, 'User', refetch));
  const userUpdate = useUpdateUser(onMutation(MutationAction.UPDATE, 'User', refetch));
  const userActivate = useActivateUsers(onMutation(MutationAction.UPDATE, 'Activation', refetch));
  const userDeactivate = useDeactivateUsers(
    onMutation(MutationAction.UPDATE, 'Activation', refetch),
  );

  const filterLabels = useMemo(() => {
    if (
      allUsersCount &&
      adminUsersCount &&
      deactivatedUsersCount &&
      memberUsersCount &&
      staffUsersCount &&
      mentorUsersCount &&
      menteeUsersCount
    ) {
      const filterValues = [
        { label: `All Users (${allUsersCount.count})`, value: 'all' },
        {
          label: `Admins (${adminUsersCount.count})`,
          value: 'admins',
        },
        {
          label: `Deactivated (${deactivatedUsersCount.count})`,
          value: 'deactivated',
        },
        {
          label: `Members (${memberUsersCount.count})`,
          value: 'members',
        },
        {
          label: `GrowthZone Staff (${staffUsersCount.count})`,
          value: 'staff',
        },
      ];

      // If the mentorship feature flag is enabled, we add the mentor and mentee filters
      if (mentorshipEnabled || isSuperAdmin) {
        filterValues.push(
          { label: `Mentors (${mentorUsersCount.count})`, value: 'mentors' },
          { label: `Mentees (${menteeUsersCount.count})`, value: 'mentees' },
        );
      }
      return filterValues;
    }
    return null;
  }, [
    allUsersCount,
    adminUsersCount,
    deactivatedUsersCount,
    memberUsersCount,
    mentorUsersCount,
    menteeUsersCount,
  ]);

  const { ref, inView } = useInView({
    threshold: 0,
    initialInView: false,
  });

  useEffect(() => {
    if (inView && !isFetchingUsers) {
      fetchNextPage();
    }
  }, [inView]);

  const toggleUserSelection = (userId: string) => {
    setSelectedUsers((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
    );
    if (!isBulkSelecting) {
      setConfirmDialogOpen(true);
    }
  };

  const submitActivationAction = () => {
    const users = allUsers?.pages?.flat().filter((u) => selectedUsers.includes(u.id));
    if (users && users.length > 0) {
      userActivate.mutate({
        platformId: platformId,
        data: {
          user_ids: selectedUsers,
        },
      });
    }
  };

  const submitDeactivationAction = (values: UserDeactivation) => {
    const users = allUsers?.pages?.flat().filter((u) => selectedUsers.includes(u.id));
    if (users && users.length > 0) {
      userDeactivate.mutate({
        platformId: platformId,
        data: {
          users: users.map((u) => u.id),
          reason: values.reason,
          description: values.description,
        } as UserDeactivation,
      });
      setSelectedUsers([]);
    }
  };

  return (
    <Container>
      <Card sx={{ p: 2 }}>
        <Stack direction={'row'} justifyContent='space-between' alignItems={'center'}>
          <Typography variant='h5' fontWeight='bold'>
            Users
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={isBulkSelecting}
                onChange={(e) => {
                  setIsBulkSelecting(e.target.checked);
                  setSelectedUsers([]);
                }}
              />
            }
            label={<Typography fontSize='12px'>Batch Select</Typography>}
            sx={{ mr: 3 }}
          />
        </Stack>
        <Box>
          <Typography variant='body1' pt={1} pb={1} fontSize={'14px'}>
            Manage Users in the Community
          </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Grid container>
          {isBulkSelecting && (
            <Grid item xs={3}>
              <Box my={1} display={'flex'} mr={1}>
                <AccessibleCheckbox
                  selectedUsers={new Set(selectedUsers)}
                  totalUsers={allUsersCount?.count || 0}
                  allUserIds={allUsers?.pages?.flat().map((u) => u.id) || []}
                  onChange={(newSelectedUsers) => {
                    setSelectedUsers(Array.from(newSelectedUsers));
                  }}
                />
                <Select
                  fullWidth
                  id='multi_user_select'
                  value={0}
                  onChange={(e) => {
                    if (e.target.value === 1) {
                      setConfirmDialogOpen(true);
                    } else if (e.target.value === 2) {
                      setSuccessfullySubmitted(false);
                      setDeactivateUserOpen(true);
                    }
                  }}
                  size='small'
                  disabled={selectedUsers.length === 0}
                  sx={searchStyles}
                >
                  <MenuItem disabled value={0}>
                    Choose Action
                  </MenuItem>
                  <MenuItem value={1}>Activate</MenuItem>
                  <MenuItem value={2}>Deactivate</MenuItem>
                </Select>
              </Box>
            </Grid>
          )}
          <Grid item xs={isBulkSelecting ? 9 : 12}>
            <DefaultSearchSortFilter
              buttonDisabled={false}
              showButton={!isBulkSelecting}
              buttonText={'Create User'}
              onClickButton={() => setCreateNewOpen(true)}
              setSearch={setSearchValue}
              showSort={true}
              setSort={setSort}
              sort={sort}
              showFilter={false}
              sortOptions={[
                { label: 'First Name', value: 'first_name' },
                { label: 'Last Name', value: 'last_name' },
                { label: 'Email', value: 'email' },
                { label: 'Role', value: 'role' },
              ]}
            />
          </Grid>
        </Grid>
        {/* Filter Chips */}
        <Box
          sx={{
            mt: 2,
            gap: 1,
            overflowX: 'hidden',
          }}
        >
          <ScrollableChips
            filterLabels={filterLabels}
            selectedFilter={selectedFilter}
            setSelectedFilter={setSelectedFilter}
          />
        </Box>
        <Box overflow={'auto'} mt={2}>
          <Table size='small'>
            {/* Table Header */}
            <TableHead>
              <TableRow>
                {isBulkSelecting && <TableCell></TableCell>}
                <TableCell width={60}></TableCell>
                <TableCell>
                  <Typography variant='subtitle2' fontWeight='bold'>
                    First Name
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='subtitle2' fontWeight='bold'>
                    Last Name
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='subtitle2' fontWeight='bold'>
                    Email Address
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='subtitle2' fontWeight='bold'>
                    Role
                  </Typography>
                </TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!isLoadingUsers && allUsers?.pages?.[0]?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography variant='body2' color='textSecondary' p={2}>
                      No users found.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {platformId &&
                allUsers &&
                allUsers.pages.length > 0 &&
                allUsers.pages?.map((page, i) =>
                  page.map((user, i) => {
                    return (
                      <UserListItem
                        deactivationReason={
                          userDeactivations?.find((ud) => ud.users.find((u) => u === user.id))
                            ?.reason
                        }
                        deactivationDescription={
                          userDeactivations?.find((ud) => ud.users.find((u) => u === user.id))
                            ?.description || ''
                        }
                        platformId={platformId}
                        userUpdate={userUpdate}
                        setUserToEdit={setCurrentUser}
                        setViewEditOpen={setViewEditOpen}
                        key={user.id}
                        user={user}
                        isSelected={selectedUsers.includes(user.id)}
                        isBulkSelecting={isBulkSelecting}
                        toggleUserSelection={toggleUserSelection}
                        singleUserDisable={(userId) => {
                          const user = allUsers.pages?.flat().find((u) => u.id === userId);
                          setCurrentUser(user || null);
                          if (user) {
                            setSuccessfullySubmitted(false);
                            setDeactivateUserOpen(true);
                          }
                        }}
                        isViewerStaff={isViewerStaff || false}
                      />
                    );
                  }),
                )}
              {isLoadingUsers && (
                <>
                  <UserListSkeleton />
                  <UserListSkeleton />
                </>
              )}
              {isFetchingUsers && <UserListSkeleton />}
              {!isFetchingUsers && allUsers?.pages[allUsers.pages.length - 1].length !== 0 ? (
                <TableRow ref={ref} className='pagination-element' sx={{ height: 20 }}></TableRow>
              ) : (
                <TableRow sx={{ height: 20 }} />
              )}
            </TableBody>
          </Table>
        </Box>
      </Card>
      <RightExpandableDrawer open={viewEditOpen} onClose={() => setViewEditOpen(false)}>
        <DrawerHeader title={'View/Edit Details'} />
        <EditUserForm
          currentUser={currentUser}
          handleClose={() => setViewEditOpen(false)}
          handleSave={(user: JunoUser) => {
            const updateUser = async () => {
              await userUpdate.mutate({
                platformId: platformId,
                userId: user.id,
                data: user,
              });
              setCurrentUser(null);
              setViewEditOpen(false);
            };
            updateUser();
          }}
          isViewerStaff={isViewerStaff}
          isMentorshipEnabled={mentorshipEnabled || isSuperAdmin}
        />
      </RightExpandableDrawer>
      <RightExpandableDrawer open={createNewOpen} onClose={() => setCreateNewOpen(false)}>
        <DrawerHeader title={'Create New'} />
        <EditUserForm
          isNewUser={true}
          currentUser={{} as JunoUser}
          handleClose={() => setCreateNewOpen(false)}
          handleSave={(user: JunoUser) => {
            const createUser = async () => {
              await userCreate.mutate({
                platformId: platformId,
                data: user,
              });
              setCurrentUser(null);
              setCreateNewOpen(false);
            };
            createUser();
          }}
          isViewerStaff={isViewerStaff}
          isMentorshipEnabled={mentorshipEnabled || isSuperAdmin}
        />
      </RightExpandableDrawer>
      <DeactivateUserDialog
        successfullySubmitted={successfullySubmitted}
        isSubmitting={userDeactivate.isPending}
        isBulkEditing={isBulkSelecting}
        open={deactivateUserOpen}
        user={currentUser || ({} as JunoUser)}
        handleSubmit={(values: UserDeactivation) => {
          if (isBulkSelecting) {
            submitDeactivationAction(values);
          } else {
            userDeactivate.mutate({
              platformId: platformId,
              data: {
                users: isBulkSelecting ? selectedUsers : [currentUser?.id],
                reason: values.reason,
                description: values.description,
              } as UserDeactivation,
            });
          }
        }}
        handleClose={() => {
          setDeactivateUserOpen(false);
          setIsBulkSelecting(false);
          setSelectedUsers([]);
          setSuccessfullySubmitted(false);
        }}
      />
      <ConfirmDialog
        open={confirmDialogOpen}
        onSave={() => {
          if (isBulkSelecting) {
            submitActivationAction();
          } else {
            userActivate.mutate({
              platformId: platformId,
              data: {
                user_ids: selectedUsers,
              },
            });
          }
          setConfirmDialogOpen(false);
        }}
        onCancel={() => setConfirmDialogOpen(false)}
        title={`Are you sure you want to reactivate ${
          selectedUsers.length > 1 ? 'these users' : 'this user'
        }?`}
      />
    </Container>
  );
};

export default UsersPanel;
